import { fmtCompactEur, fmtPct } from '../formatters.js';

export default function StablecoinEconomics({ inputs, summary }) {
  const last = summary.lastMonth || {};
  const net = summary.netStablecoinEconomics ?? 0;
  const userApy = Object.fromEntries(
    Object.entries(inputs.tiers).map(([k, tier]) => [
      k,
      Math.max(0, (inputs.stablecoin?.smartContractYieldApy ?? 0) - (tier.pulsarYieldSpread ?? 0)),
    ]),
  );

  return (
    <section className="rounded-card border border-carbon-100 bg-white p-7">
      <div className="flex items-baseline justify-between gap-4 mb-5">
        <div>
          <p className="caption text-carbon-400">Stablecoin economics</p>
          <h2 className="h3 mt-2">Yield must cover wallet operations.</h2>
        </div>
        <p className={`label num-tabular ${net >= 0 ? 'text-mint-800' : 'text-rose-700'}`}>
          {fmtCompactEur(net)} over 36 months
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Metric
          label="Invest balance"
          value={fmtCompactEur(last.stablecoinInvestBalance ?? 0)}
          sub="M36 active balance"
        />
        <Metric
          label="Pulsar yield spread"
          value={fmtCompactEur(summary.stablecoinYieldRev ?? 0)}
          sub="36-month retained yield"
        />
        <Metric
          label="Wallet subsidy"
          value={fmtCompactEur(summary.walletOperationCost ?? 0)}
          sub="Covered operation cost"
        />
        <Metric
          label="Smart contract yield"
          value={fmtPct(inputs.stablecoin?.smartContractYieldApy ?? 0, 1)}
          sub="Gross APY assumption"
        />
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-2">
        {['free', 'pro'].map((key) => {
          const tier = inputs.tiers[key];
          return (
            <div key={key} className="rounded-box border border-carbon-100 p-4">
              <p className="caption text-carbon-400">{tier.displayName}</p>
              <div className="mt-3 flex items-baseline justify-between gap-3">
                <span className="label text-carbon-500">User Earn APY</span>
                <span className="label text-carbon-800 num-tabular">{fmtPct(userApy[key], 2)}</span>
              </div>
              <div className="mt-2 flex items-baseline justify-between gap-3">
                <span className="label text-carbon-500">Pulsar spread</span>
                <span className="label text-carbon-800 num-tabular">
                  {fmtPct(tier.pulsarYieldSpread, 2)}
                </span>
              </div>
              <div className="mt-2 flex items-baseline justify-between gap-3">
                <span className="label text-carbon-500">Monthly allowance cap</span>
                <span className="label text-carbon-800 num-tabular">
                  {fmtCompactEur(tier.walletOperationCostCap)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Metric({ label, value, sub }) {
  return (
    <div className="rounded-box bg-carbon-50 border border-carbon-100 p-4">
      <p className="caption text-carbon-400">{label}</p>
      <p className="text-2xl font-semibold tracking-h2 num-tabular mt-2 leading-none">{value}</p>
      <p className="label text-carbon-500 mt-3">{sub}</p>
    </div>
  );
}
